import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Button,
  Group,
  Stack,
  Paper,
  Text,
  Table,
  Loader,
  Badge,
  Select,
  Accordion,
  Alert,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconRefresh, IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface ScheduleRun {
  id: string;
  created_at: string;
  balance_start: string;
  balance_end: string;
  schedule_start: string;
  schedule_end: string;
  direction: string;
  total_shifts: number;
  total_hours: number;
  fixed_shifts: number;
  fixed_hours: number;
  max_deviation: number;
  min_weekly_hours: number;
}

interface WeekStep {
  step_order: number;
  step_name: string;
  employees: {
    employee_id: number;
    employee_name: string;
    shift_count: number;
    total_hours: number;
    final_target: number;
    diff_from_target: number;
  }[];
  shifts: {
    employee_id: number;
    employee_name: string;
    shift_date: string;
    start_time: string;
    end_time: string;
    hours: number;
    reason: string;
  }[];
}

const STEP_DESCRIPTIONS: Record<number, string> = {
  1: 'Fixed weekday mornings (Tue/Wed/Thu)',
  2: 'Monday rotation shifts (3-of-9 rule)',
  3: 'Minimum guarantee + preferred shifts',
  4: 'Additional preferred shifts',
  5: 'Balanced hours (fill by target gap)',
  6: 'Rebalance (swap to improve balance)',
  7: 'Min shift enforce (ensure 2 shifts/week)',
  8: 'Preference optimization (swap for preferences)',
};

export default function ScheduleStepsPage() {
  const [startDate, setStartDate] = useState<Date>(
    dayjs().startOf('week').toDate()
  );
  const [weeks, setWeeks] = useState<string>('4');
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState<ScheduleRun | null>(null);
  const [weeklySteps, setWeeklySteps] = useState<Map<string, WeekStep[]>>(new Map());

  const fetchScheduleRun = async () => {
    setLoading(true);
    try {
      const endDate = dayjs(startDate).add(parseInt(weeks), 'week').subtract(1, 'day');
      
      // Get latest run for this period
      const runResponse = await fetch(
        `${API_BASE}/api/shifts/runs`
      );
      
      if (!runResponse.ok) throw new Error('Failed to fetch runs');
      
      const runsData = await runResponse.json();
      const runs = (runsData.runs as ScheduleRun[]) || [];
      
      if (runs.length === 0) {
        notifications.show({
          title: 'No Schedule Runs',
          message: 'No schedule runs found. Generate a schedule first.',
          color: 'orange',
        });
        setRun(null);
        setWeeklySteps(new Map());
        return;
      }
      
      // Find run that covers this period
      const matchingRun = runs.find((r: ScheduleRun) => 
        r.schedule_start <= dayjs(startDate).format('YYYY-MM-DD') &&
        r.schedule_end >= endDate.format('YYYY-MM-DD')
      );
      
      if (!matchingRun) {
        notifications.show({
          title: 'No Schedule Run',
          message: 'No schedule run found for this period. Generate a schedule first.',
          color: 'orange',
        });
        setRun(null);
        setWeeklySteps(new Map());
        return;
      }
      
      setRun(matchingRun);
      
      // Fetch step-by-step data for each week
      const runId = matchingRun.id;
      
      // Get all steps for this run
      const stepsResponse = await fetch(
        `${API_BASE}/api/shifts/run/${runId}`
      );
      
      if (!stepsResponse.ok) throw new Error('Failed to fetch step data');
      
      const stepsData = await stepsResponse.json();
      
      // Group by week
      const weekMap = new Map<string, WeekStep[]>();
      
      // Get unique weeks from weekTargets
      const weekStarts = Array.from(new Set(stepsData.weekTargets.map((w: any) => w.week_start as string))).sort() as string[];
      
      for (const weekStart of weekStarts) {
        // Get steps for this week
        const weekStepsResponse = await fetch(
          `${API_BASE}/api/shifts/run/${runId}/week/${weekStart}`
        );
        
        if (!weekStepsResponse.ok) continue;
        
        const weekData = await weekStepsResponse.json();
        
        // Transform into WeekStep format
        const steps: WeekStep[] = [];
        
        for (const stepResult of weekData.steps || []) {
          const stepShifts = weekData.step_shifts.filter(
            (s: any) => s.step_order === stepResult.step_order
          );
          
          // Calculate per-employee totals for this step
          const employeeTotals = new Map<number, any>();
          
          for (const shift of stepShifts) {
            if (!employeeTotals.has(shift.employee_id)) {
              const target = weekData.week_targets.find(
                (t: any) => t.employee_id === shift.employee_id
              );
              
              employeeTotals.set(shift.employee_id, {
                employee_id: shift.employee_id,
                employee_name: shift.employee_name || `Employee ${shift.employee_id}`,
                shift_count: 0,
                total_hours: 0,
                final_target: target?.final_target || 0,
                diff_from_target: 0,
              });
            }
            
            const empTotal = employeeTotals.get(shift.employee_id)!;
            empTotal.shift_count++;
            empTotal.total_hours += shift.hours;
            empTotal.diff_from_target = empTotal.total_hours - empTotal.final_target;
          }
          
          steps.push({
            step_order: stepResult.step_order,
            step_name: stepResult.step_name,
            employees: Array.from(employeeTotals.values()),
            shifts: stepShifts.map((s: any) => ({
              employee_id: s.employee_id,
              employee_name: s.employee_name || `Employee ${s.employee_id}`,
              shift_date: s.shift_date,
              start_time: s.start_time,
              end_time: s.end_time,
              hours: s.hours,
              reason: s.reason,
            })),
          });
        }
        
        weekMap.set(weekStart, steps);
      }
      
      setWeeklySteps(weekMap);
      
    } catch (error: any) {
      console.error('Error fetching schedule run:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load schedule steps',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduleRun();
  }, []);

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="lg">
          <Loader size="lg" />
          <Text>Loading schedule steps...</Text>
        </Stack>
      </Container>
    );
  }

  if (!run) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="lg">
          <Title order={2}>No Schedule Run Found</Title>
          <Text>Generate a schedule in the Assigned Shifts page first.</Text>
        </Stack>
      </Container>
    );
  }

  const weekStarts = Array.from(weeklySteps.keys()).sort();

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Schedule Algorithm Steps</Title>
          <Group>
            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={(date) => date && setStartDate(date)}
              leftSection={<IconCalendar size={16} />}
            />
            <Select
              label="Weeks"
              value={weeks}
              onChange={(value) => value && setWeeks(value)}
              data={[
                { value: '1', label: '1 week' },
                { value: '2', label: '2 weeks' },
                { value: '4', label: '4 weeks' },
                { value: '8', label: '8 weeks' },
              ]}
            />
            <Button
              onClick={fetchScheduleRun}
              loading={loading}
              leftSection={<IconRefresh size={16} />}
              mt="auto"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Run Summary */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">Run Summary</Title>
          <Group gap="xl">
            <div>
              <Text size="sm" c="dimmed">Run ID</Text>
              <Text ff="monospace">{run.id.substring(0, 8)}...</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">Created</Text>
              <Text>{dayjs(run.created_at).format('MMM D, YYYY HH:mm')}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">Direction</Text>
              <Badge color={run.direction === 'FORWARD' ? 'blue' : 'grape'}>
                {run.direction}
              </Badge>
            </div>
            <div>
              <Text size="sm" c="dimmed">Period</Text>
              <Text>{run.schedule_start} to {run.schedule_end}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">Total Shifts</Text>
              <Text>{run.total_shifts}</Text>
            </div>
            <div>
              <Text size="sm" c="dimmed">Max Deviation</Text>
              <Text>{run.max_deviation.toFixed(2)}h</Text>
            </div>
          </Group>
        </Paper>

        {/* Info Alert */}
        <Alert icon={<IconInfoCircle />} title="How to read this page" color="blue">
          <Text size="sm">
            This page shows how the scheduling algorithm built the schedule step-by-step.
            Each week is processed through 8 steps, with assignments accumulating at each step.
            The final step shows the complete schedule for that week.
          </Text>
        </Alert>

        {/* Weekly Steps */}
        <Accordion multiple>
          {weekStarts.map((weekStart) => {
            const steps = weeklySteps.get(weekStart) || [];
            const weekEnd = dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD');
            const finalStep = steps[steps.length - 1];
            const totalShifts = finalStep?.shifts.length || 0;
            const totalHours = finalStep?.employees.reduce((sum, e) => sum + e.total_hours, 0) || 0;
            
            return (
              <Accordion.Item key={weekStart} value={weekStart}>
                <Accordion.Control>
                  <Group justify="space-between">
                    <div>
                      <Text fw={500}>
                        Week of {dayjs(weekStart).format('MMM D')} - {dayjs(weekEnd).format('MMM D, YYYY')}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {totalShifts} shifts • {totalHours.toFixed(1)} hours
                      </Text>
                    </div>
                    <Badge>{steps.length} steps</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="lg">
                    {steps.map((step) => (
                      <Paper key={step.step_order} p="md" withBorder>
                        <Group justify="space-between" mb="md">
                          <div>
                            <Group gap="xs">
                              <Badge size="lg" variant="filled">Step {step.step_order}</Badge>
                              <Title order={4}>{step.step_name}</Title>
                            </Group>
                            <Text size="sm" c="dimmed" mt={4}>
                              {STEP_DESCRIPTIONS[step.step_order] || 'Algorithm step'}
                            </Text>
                          </div>
                          <Group>
                            <Badge variant="light">{step.shifts.length} shifts added</Badge>
                            <Badge variant="light">
                              {step.employees.reduce((sum, e) => sum + e.total_hours, 0).toFixed(1)}h total
                            </Badge>
                          </Group>
                        </Group>

                        {/* Employee Totals */}
                        <Title order={5} mb="xs">Employee Totals After This Step</Title>
                        <Table mb="md">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Employee</Table.Th>
                              <Table.Th>Shifts</Table.Th>
                              <Table.Th>Hours</Table.Th>
                              <Table.Th>Target</Table.Th>
                              <Table.Th>Diff</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {step.employees.map((emp) => (
                              <Table.Tr key={emp.employee_id}>
                                <Table.Td>{emp.employee_name}</Table.Td>
                                <Table.Td>{emp.shift_count}</Table.Td>
                                <Table.Td>{emp.total_hours.toFixed(1)}</Table.Td>
                                <Table.Td>{emp.final_target.toFixed(1)}</Table.Td>
                                <Table.Td>
                                  <Text
                                    c={
                                      emp.diff_from_target > 0
                                        ? 'green'
                                        : emp.diff_from_target < 0
                                        ? 'red'
                                        : undefined
                                    }
                                  >
                                    {emp.diff_from_target > 0 ? '+' : ''}
                                    {emp.diff_from_target.toFixed(1)}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>

                        {/* Shifts Added in This Step */}
                        {step.shifts.length > 0 && (
                          <>
                            <Title order={5} mb="xs">
                              Shifts Added in This Step ({step.shifts.length})
                            </Title>
                            <Table>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Employee</Table.Th>
                                  <Table.Th>Date</Table.Th>
                                  <Table.Th>Time</Table.Th>
                                  <Table.Th>Hours</Table.Th>
                                  <Table.Th>Reason</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {step.shifts.map((shift, idx) => (
                                  <Table.Tr key={idx}>
                                    <Table.Td>{shift.employee_name}</Table.Td>
                                    <Table.Td>
                                      {dayjs(shift.shift_date).format('ddd MMM D')}
                                    </Table.Td>
                                    <Table.Td>
                                      {shift.start_time} - {shift.end_time}
                                    </Table.Td>
                                    <Table.Td>{shift.hours.toFixed(1)}h</Table.Td>
                                    <Table.Td>
                                      <Text size="sm" c="dimmed">
                                        {shift.reason}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Stack>
    </Container>
  );
}
